import * as LDClient from 'launchdarkly-js-client-sdk'
import { castFlagValue } from '@/features/feature-toggles/toggle-util'
import { fromEvent, type Observable } from 'rxjs'
import env from '@/infra/env'
import type {
  FeatureToggleClient,
  FeatureToggleUser,
  FlagValue,
} from '@/features/feature-toggles/toggle-client'

/**
 * Wrapper around the live LaunchDarkly client.
 */
export class LiveLDClient implements FeatureToggleClient {
  private constructor(private client: LDClient.LDClient) {}

  /** @inheritdoc */
  variation<T extends FlagValue>(key: string, defaultValue: T): T {
    return castFlagValue(this.client.variation(key, defaultValue), defaultValue)
  }

  /** @inheritdoc */
  flagChanges(key: string): Observable<unknown> {
    return fromEvent(this.client, `change:${key}`)
  }

  /** @inheritdoc */
  async identify(user: FeatureToggleUser): Promise<void> {
    await this.client.identify({
      key: user.id,
      email: user.email,
      custom: {
        workspace_slugs: user.workspaceSlugs,
      },
    })
  }

  /**
   * Creates a live LD client.
   */
  static create() {
    const anonymousContext: LDClient.LDContext = {
      key: 'anonymous',
      anonymous: true,
    }
    const ldClient = LDClient.initialize(
      env.LAUNCHDARKLY_CLIENT_SIDE_ID,
      anonymousContext,
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-explicit-any
        bootstrap: (window as any).__ldFlagsState,
      },
    )
    return new LiveLDClient(ldClient)
  }
}
